<script lang="ts" setup>
    import { reactive, ref, computed, onBeforeMount, nextTick } from 'vue';
    import '../../../css/choice.css';
    import { confirmDialog, popAlert } from '../../lib/Notification';
    import { getQueryString } from '../../lib/QueryString';
    import { ChoiceApi } from '../../LibMain';
    import { IdMeScopeType, UserProviderResponse } from '../../lib/Api/IdMe';
    import { DisplayStyle, ButtonDisplaySize, MessageSectionType } from '../../Enums/UiEnums';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { IProviderSearchItem } from '../../lib/ChoiceApi';
    import { AppScreens, PrivilegeType, EnvironmentType, ProductType } from '../../Enums/ChoiceEnums';
    import { NoTzDate } from '../../lib/DateUtils';
    import _ from 'lodash';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import TitledRow from '../../components/TitledRow.vue'
    import FoldingPanel from '../../components/FoldingPanel.vue'
    import SearchProvider from '../../components/Search/SearchProvider.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue';
    import PageTitle from '../../components/PageTitle.vue'
    import Priviledge from '../../components/Privilege.vue'
    import DeaNumberInfo from '../IdMeVerify/DeaNumberInfo.vue';
    import ModalDialog from '../../components/ModalDialog.vue'
    import TextInput from '../../components/TextInput.vue'
    import { serverEnvironment } from '../../lib/Environment';
    import { useCompanySettingsStore } from '../../lib/Stores/CompanySettingsStore'
    import { initPinia } from '../../lib/PiniaMgr';

    const compData = reactive<{
        user_id?: string
        titleWidth: number,
        providerInfo?: UserProviderResponse,
        showVerifyOptions: boolean,
        verifyPanelIsOpen: boolean,
        detailsPanelIsOpen: boolean,
        isProductActive_IdMeEpcs?: boolean,
        disconnectProviderModal:
        {
            show: boolean
            reason?: string
        },
        activeCredentials:{
            identity?: boolean,
            epcs?: boolean
        },
        pageInitialized?: boolean
    }>({
        titleWidth: 2,
        showVerifyOptions: false,
        verifyPanelIsOpen: false,
        detailsPanelIsOpen: true,
        disconnectProviderModal:
        {
            show: false
        },
        activeCredentials:{}
    });

    const disconnectProviderModalReason = ref();
    const idMeSolidVerifyButtonSrc = "/officemd/images/IdMe/VerifyWithIdMeSolid.svg";
    const idMeSolidOutlinedButtonSrc = "/officemd/images/IdMe/VerifyWithIdMeOutlined.svg";

    const companySettingsStore = useCompanySettingsStore(initPinia());

    onBeforeMount(async () => {
        compData.user_id = getQueryString("user");
        if (!compData.user_id)
        {
            await popAlert("User ID was not provided");
            return;
        }
        await getProviderInfo();

        compData.isProductActive_IdMeEpcs = await companySettingsStore.isProductActive(ProductType.IdMeEpcs);

        compData.pageInitialized = true;
    });

    async function getProviderInfo(checkActiveCredentials: boolean = true): Promise<boolean | undefined>
    {
        if (!compData.user_id || 0 === compData.user_id.trim().length)
        {
            await popAlert("user_id is invalid");
            return;
        }

        const providerInfo = await ChoiceApi.idMe.getProvider(compData.user_id);
        if (!providerInfo)
        {
            return;
        }
        compData.providerInfo = providerInfo;

        // Do we have ID.me api info?
        if (checkActiveCredentials)
        {
            await verifyActiveCredentials();
        }

        return true;
    }

    // Verify ID.me has all the data needed to indicate someone has FULLY valid EPCS info
    const idMeFullyValidEpcsInfo = computed<boolean>(() =>{
        const deaNumbersInIdMe = idmeProviderInfo.value?.deaNumbers;

        // We must have DEA numbers in ID.me and be validated
        return ((deaNumbersInIdMe?.length ?? 0) > 0) && true === compData.activeCredentials.epcs;
    })

    async function verifyActiveCredentials()
    {
        const apiInfo = compData.providerInfo?.idmeProviderInfo?.api;
        if (!apiInfo)
        {
            return;
        }

        // Reset credential status data
        compData.activeCredentials.identity = false;
        compData.activeCredentials.epcs = false;

        // Check if ID.me credentials are valid
        const respIdentity =  await ChoiceApi.idMe.verifyActiveCredentials(
            apiInfo.securityUsersId, 
            apiInfo.providerNpi ?? "",
            IdMeScopeType.VerifyIdentity
        );
        compData.activeCredentials.identity = respIdentity?.verified;

        // See if we have DEA numbers available from ID.me before trying to verify EPCS credentials
        if (!idMeFullyValidEpcsInfo.value)
        {
            const respEpcs = await ChoiceApi.idMe.verifyActiveCredentials(
                apiInfo.securityUsersId, 
                apiInfo.providerNpi ?? "",
                IdMeScopeType.VerifyEPCS
            );
            compData.activeCredentials.epcs = respEpcs?.verified;
        }

        // Decide if the verify panel should be open or not
        if (showVerifyOptions.value)
        {
            if (!compData.activeCredentials.epcs || !compData.activeCredentials.identity)
                compData.verifyPanelIsOpen = true;
        }
    }

    async function saveProviderAssociation(){
        if (!omdProviderInfo.value?.entityId)
        {
            alert("omdProviderInfo.value?.entityId is invalid");
            return;
        }
        if (!compData.user_id)
        {
            alert("compData.user_id is invalid");
            return;
        }

        await ChoiceApi.idMe.saveProvider(omdProviderInfo.value?.entityId, compData.user_id);
    }

    async function deleteProviderAssociation() {
        if (!omdProviderInfo.value?.entityId)
        {
            alert("omdProviderInfo.value?.entityId is invalid");
            return;
        }
        if (!compData.user_id)
        {
            alert("compData.user_id is invalid");
            return;
        }

        if (!await confirmDialog("Are you sure you want to remove the association?"))
            return;

        const providerInfo = await ChoiceApi.idMe.deleteProvider(omdProviderInfo.value.entityId, compData.user_id);
        updateProviderData(providerInfo);
    }


    const showVerifyEPCSButton = computed(() => {
        if (!haveChoiceDeaNumber.value)
            return false;
        if (idMeFullyValidEpcsInfo.value)
            return false;
        return true;
    });

    const idmeProviderInfo = computed(() => compData.providerInfo?.idmeProviderInfo?.idMe);
    const omdProviderInfo = computed(() => compData.providerInfo?.idmeProviderInfo?.choiceProvider);
    const idMeIdentityVerified = computed<boolean>(() => !!idmeProviderInfo.value?.verifiedDate );

    const idMeDeaNumberCount = computed<number>(() => idmeProviderInfo.value?.deaNumbers?.length ?? 0);
    const haveChoiceDeaNumber = computed<boolean>(() => (compData.providerInfo?.idmeProviderInfo?.choiceDeaNumbers?.length ?? 0) > 0);
    const idMeVerifiedEPSC = computed(() => idMeDeaNumberCount.value > 0);
    const idMeDetailsColumnClass = computed(() => idMeIdentityVerified.value || idMeVerifiedEPSC.value ? undefined : "IdMeIdentityNotVerified");

    function updateProviderData(providerInfo?: UserProviderResponse)
    {
        compData.providerInfo = providerInfo;
    }

    async function onChangeProvider(result?: IProviderSearchItem) {
        
        if (undefined == result)
        {
            updateProviderData();
            return;
        }

        if (result.entity_id)
        {
            // Clear credentials state
            compData.activeCredentials.identity = undefined;
            compData.activeCredentials.epcs = undefined;

            if (!compData.user_id)
            {
                alert("compData.user_id is undefined");
                return;
            }
            
            if (!await confirmDialog(`Would you like to associate <b>${result.provider_name}</b> with the current user (${compData.user_id})`))
            return;

            const providerInfo = await ChoiceApi.idMe.saveProvider(result.entity_id, compData.user_id);
            updateProviderData(providerInfo);

            // Check credentials state
            await verifyActiveCredentials();
        }
    }

    interface IFullDeaNumberInfo {deaNumber: string, state: string, idMeExpDate?: NoTzDate, spiEnabled?: boolean, inIdMe?: boolean, inChoice?: boolean };

    // Get the combined set of DEA numbers/states
    const allDeaNumbersAndStates = computed<IFullDeaNumberInfo[]>(() => {
        const idMeDeaNumers = idmeProviderInfo.value?.deaNumbers?.map(deaNumber => {return { deaNumber: deaNumber.number, state: deaNumber.state}})
        const allItems: IFullDeaNumberInfo[] = [];
        if (idMeDeaNumers)
            allItems.push(...idMeDeaNumers);
        const omdDeaNumbers = compData.providerInfo?.idmeProviderInfo?.choiceDeaNumbers?.map(deaNumber => {return {deaNumber: deaNumber.deaNumber, state: deaNumber.deaState}});
        if (omdDeaNumbers)
            allItems.push(...omdDeaNumbers);

        const allUniqueItems = _.uniqBy(allItems, i => `${i.deaNumber}/${i.state}`);

        for(let item of allUniqueItems)
        {
            const idMeDeaNumberInfo = idmeProviderInfo.value?.deaNumbers?.find(deaNumber => deaNumber.number === item.deaNumber && deaNumber.state === item.state)
            if (idMeDeaNumberInfo)
            {
                item.inIdMe = true;
                item.idMeExpDate = idMeDeaNumberInfo?.expires_at;
            }

            const choiceDeaNumberInfo = compData.providerInfo?.idmeProviderInfo?.choiceDeaNumbers?.find(deaNumber => deaNumber.deaNumber === item.deaNumber && deaNumber.deaState === item.state);
            if (choiceDeaNumberInfo)
            {
                item.inChoice = true;
                item.spiEnabled = choiceDeaNumberInfo.spiEnabled;
            }
        }
        return allUniqueItems;
    });

    // Get confirmation data
    const confirmationInfo = computed(() =>
    {
        if (true !== idmeProviderInfo.value?.npiConfirmed)
            return;

        return {
            confirmDate: idmeProviderInfo.value?.npiConfirmDate,
            confirmedByUser: idmeProviderInfo.value?.npiConfirmUser
        }
    });

    // If we're in production don't allow a mis-match between ID.me and Choice for NPI
    //  In testing we have to allow it since ID.me uses the same NPI number all the time
    const mismatchedNpiNumbersInProduction = computed(() => {
        if (EnvironmentType.Production === serverEnvironment())
            return idmeProviderInfo.value?.npi !== omdProviderInfo.value?.npi

        // Ignore mis-matched NPI numbers in QA/Dev
        return false;
    });

    const showConfirmButton = computed<boolean>(() => 
    {
        if (mismatchedNpiNumbersInProduction.value)
            return false;

        if (!confirmationInfo.value)
        {
            if (idMeFullyValidEpcsInfo.value)
            {
                const showResult = true === compData.providerInfo?.choiceDeaNumbersMatchIdMe;

                // If we're testing and DEA numbers don't match, allow the confirm button to be displayed
                //  ID.Me uses the same DEA number when testing
                if (false === showResult && EnvironmentType.Production !== serverEnvironment())
                {
                    return true;
                }
                return showResult;
            }
            
            if (compData.activeCredentials.identity)
            {
                return true;
            }
        }

        return false;
    });

    const showDeactivateButton = computed<boolean>(() => 
        !!confirmationInfo.value
    );

    async function confirmProviderInfo()
    {
        const npi = omdProviderInfo.value?.npi;
        if (!npi)
        {
            await popAlert("Medical NPI is invalid");
            return;
        }
        const resp = await ChoiceApi.idMe.confirmProvider(npi);
        if (!resp)
        {
            await popAlert("Problem confirming the NPI");
            return;
        }

        // Update provider info
        await getProviderInfo();
    }

    async function disconnectProvider() 
    {
        const entityId = omdProviderInfo.value?.entityId;
        if (!entityId)
        {
            await popAlert("Invalid entityId");
            return;
        }
        const npi = omdProviderInfo.value?.npi;
        if (!npi)
        {
            await popAlert("Medical NPI is invalid");
            return;
        }

        const reason = compData.disconnectProviderModal.reason;
        if (!reason || 0 === reason.trim().length)
        {
            await popAlert("The reason cannot be blank");
            return;
        }
        const respDisconnectErrorMsg = await ChoiceApi.idMe.disconnectProvider(
            entityId, 
            npi,
            compData.user_id ?? "", 
            reason);
        
        if (respDisconnectErrorMsg)
        {
            await popAlert("Problem trying to disconnect provider from ID.me settings: " + respDisconnectErrorMsg);
            return;
        }

        // Clear provider info...
        compData.providerInfo = undefined;
        compData.disconnectProviderModal.show = false;
    }

    async function shownDisconnectProviderModal() {
        await nextTick();
        disconnectProviderModalReason.value.focus();
    }

    const showVerifyOptions = computed<boolean>(() =>
        undefined !== compData.providerInfo &&
        (
            false === (compData.activeCredentials.identity ?? false) 
            || false === idMeFullyValidEpcsInfo.value
        ));

    async function showDisconnectProviderModal()
    {
        compData.disconnectProviderModal.reason = ''; 
        compData.disconnectProviderModal.show = true;     
    }

</script>

<style scoped>
    .idme_button{
        height: 2em;
        cursor: pointer;
    }

    .IdMeIdentityNotVerified{
        display:none
    }

    .BlackCell{
        background-color: black;
        color:yellow;
        width: 10em;
    }
    .BlackCell::before{
        content: "Not Verified";
    }

    .VerifyButtonTitle{
        display:inline-block;
        width: 8em
    }
</style>

<template>

    <PageTitle>
        <template #title>
            ID.me Verify
        </template>
    </PageTitle>

    <TitledRow title="User" :titleWidth="compData.titleWidth">
        {{compData.user_id}}
    </TitledRow>

    <TitledRow title="Provider" :titleWidth="compData.titleWidth">
        {{ compData.providerInfo?.currentProviderSearchCompInfo?.entityName }}

        <Priviledge :screen="AppScreens.IdMeVerify" :privilege="PrivilegeType.ReadUpdateWrite" :showRestriction="true">
            <span v-if="compData.providerInfo?.currentProviderSearchCompInfo?.providerEntityId && !showDeactivateButton">
                <ActionButtonV2 :action="ActionButtonStyle.Unlink" :size="ButtonDisplaySize.Small" :performAction="deleteProviderAssociation" title="Remove association between user and provider" />
            </span>

            <!-- Don't display until the page has initialized -- we want to avoid the SearchProvider from clearing the provider info mid-initialization -->
            <template v-if="compData.pageInitialized">
                <span v-if="!compData.providerInfo?.currentProviderSearchCompInfo?.providerEntityId">
                    <SearchProvider label="" @onChange="onChangeProvider" 
                    :enableAutoSearch="true" 
                    :showClearSearch="true"
                    :resetFocus="!compData.providerInfo" />
                </span>
    
                <div v-if="!compData.providerInfo" class="mt-1">
                    <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Info">
                        Select a Provider to associate with ID.me
                    </MessageSection>        
                </div>
            </template>

        </Priviledge>
    </TitledRow>

    <Priviledge :screen="AppScreens.IdMeVerify" :privilege="PrivilegeType.ReadUpdateWrite" :showRestriction="true">
        <FoldingPanel v-model:panelOpen="compData.verifyPanelIsOpen" v-if="showVerifyOptions">
            <template #title>
                Verify
            </template>
            <template #body>
                <div class="alert alert-info" v-cloak>
                    <div>
                        <p>
                            If you have an existing ID.me account that you've used in another application, please enter your login credentials and verify your NPI. Otherwise, please create a new account and complete all steps. It is recommended you set aside uninterrupted time to complete this process.
                        </p>
                        <p>
                            You may need your mobile device to set up MFA within the verification process and complete the liveness check, which will require a camera.        
                        </p>
                    </div>
                </div>            

                <div class="mb-1" v-if="!idMeIdentityVerified">
                    <span class="VerifyButtonTitle">
                        Verify Identity
                    </span>
                    <a :href="compData.providerInfo?.verifyIdentityApiUrl" class="ms-1" title="Verify Identity">
                        <img class="idme_button" :src="idMeSolidVerifyButtonSrc" alt="Verify identity with Id.me" />
                    </a>
                </div>
    
                <div class="mb-1" v-if="showVerifyEPCSButton">
                    <span class="VerifyButtonTitle">
                        Verify EPCS
                    </span>                

                    <!-- Button when ID.me EPCS is enabled -->
                    <a v-if="true === compData.isProductActive_IdMeEpcs" :href="compData.providerInfo?.verifyEPCSApiUrl" class="ms-1" title="Verify EPCS">
                        <img class="idme_button" :src="idMeSolidOutlinedButtonSrc" alt="Verify identity with Id.me" />
                    </a>

                    <!-- Button when ID.me EPCS is not enabled -->
                    <img v-if="false === compData.isProductActive_IdMeEpcs"
                        @click="popAlert('ID.me EPCS Product is not enabled. Please contact support to begin the EPCS enrollment process.')"
                        title="Enable ID.me EPCS"
                        class="idme_button" :src="idMeSolidOutlinedButtonSrc" alt="Verify identity with Id.me" />
                </div>
            </template>
        </FoldingPanel>    
    </Priviledge>

    <FoldingPanel v-model:panelOpen="compData.detailsPanelIsOpen" class="mt-2" v-if="compData.providerInfo">
        <template #title>
            Provider Details
        </template>
        <template #body>
            <MessageSection v-if="!idMeIdentityVerified" :displayStyle="DisplayStyle.Danger">
                ID.me Identity not verified
            </MessageSection>

            <table class="table table-sm">
                <thead>
                    <th></th>
                    <th :class="idMeDetailsColumnClass">ID.Me</th>
                    <th>OfficeMD Provider Setup</th>
                </thead>
                <tbody>    
                    <tr>
                        <td>Email</td>
                        <td :class="idMeDetailsColumnClass">{{ idmeProviderInfo?.email}}</td>
                        <td>{{ omdProviderInfo?.email }}</td>
                    </tr>
    
                    <tr>
                        <td>First Name</td>
                        <td :class="idMeDetailsColumnClass">{{ idmeProviderInfo?.firstName}}</td>
                        <td>{{ omdProviderInfo?.firstName }}</td>
                    </tr>
    
                    <tr>
                        <td>Last Name</td>
                        <td :class="idMeDetailsColumnClass">{{ idmeProviderInfo?.lastName}}</td>
                        <td>{{ omdProviderInfo?.lastName }}</td>
                    </tr>
    
                    <tr>
                        <td>State</td>
                        <td :class="idMeDetailsColumnClass">{{ idmeProviderInfo?.state}}</td>
                        <td>{{ omdProviderInfo?.state }}</td>
                    </tr>

                    <tr>
                        <td>Medical NPI</td>
                        <td :class="idMeDetailsColumnClass">
                            {{ idmeProviderInfo?.npi}}
                        </td>
                        <td>{{ omdProviderInfo?.npi}}</td>
                    </tr>

                    <tr v-for="(deaNumberInfo, index) in allDeaNumbersAndStates">
                        <td>
                            <span v-if="0 === index">
                                DEA Numbers
                            </span>
                        </td>
                        <td :class="idMeDetailsColumnClass">

                            <div v-if="deaNumberInfo.inIdMe">
                                <DeaNumberInfo :deaNumber="deaNumberInfo.deaNumber" :state="deaNumberInfo.state" :expirationDate="deaNumberInfo.idMeExpDate"/>

                                <!-- Is there a matching value in Choice/OMD? -->
                                <div v-if="deaNumberInfo.inChoice">
                                    <!-- DEA numbers match but SPI not enabled -->
                                    <MessageBadge :displayStyle="DisplayStyle.Warning" v-if="!deaNumberInfo.spiEnabled" title="The SPI needs to be approved in OfficeMD">
                                        SPI Pending Approval
                                    </MessageBadge>
                                    
                                    <!-- they match and SPI enabled -->
                                    <MessageBadge :displayStyle="DisplayStyle.Success" v-if="deaNumberInfo.spiEnabled">
                                        SPI Approved
                                    </MessageBadge>
                                </div>
                            </div>
                            
                            <!-- they don't match between ID.me and OMD -->
                            <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Danger" v-if="!deaNumberInfo.inIdMe && compData.activeCredentials.epcs">
                                SPI DEA Mismatch: All DEA numbers in OfficeMD must be available from ID.me as well.
                            </MessageSection>
                        </td>
                        <td>
                            <div v-if="deaNumberInfo.inChoice">
                                <DeaNumberInfo :deaNumber="deaNumberInfo.deaNumber" :state="deaNumberInfo.state" />
                            </div>

                            <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Warning" v-if="!deaNumberInfo.inChoice">
                                Not configured
                            </MessageSection>                            
                        </td>
                    </tr>
                </tbody>
            </table>

            <ActionButtonV2 
                v-if="showConfirmButton"
                :action="ActionButtonStyle.Link"
                text="Confirm"
                :displayStyle="DisplayStyle.Success"
                :performAction="confirmProviderInfo" />

            <ActionButtonV2 
                v-if="showDeactivateButton"
                :action="ActionButtonStyle.Unlink" 
                text="Disconnect"
                :displayStyle="DisplayStyle.Danger"
                :performAction="showDisconnectProviderModal" />

            <div v-if="confirmationInfo" class="text-muted">
                Confirmed by {{ confirmationInfo.confirmedByUser }} @ {{ confirmationInfo.confirmDate }}
            </div>
    

            <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Danger" v-if="!showConfirmButton && !showDeactivateButton">
                Cannot confirm ID.me info

                <div v-if="mismatchedNpiNumbersInProduction" class="mb-2">
                    NPI number mismatch
                </div>
            </MessageSection>
        </template>
    </FoldingPanel>

    <ModalDialog v-model:show="compData.disconnectProviderModal.show" @shown="shownDisconnectProviderModal">
        <template #title>Disconnect</template>

        <MessageSection :type="MessageSectionType.Row" :displayStyle="DisplayStyle.Danger">
            This will deactivate this provider -- electronic prescriptive rights will be disabled.
        </MessageSection>

        Reason:
        <TextInput ref="disconnectProviderModalReason" v-model:textValue="compData.disconnectProviderModal.reason" :rows="4" />

        <div class="mt-2">
            <ActionButtonV2 
                v-if="(compData.disconnectProviderModal.reason?.length ?? 0) > 0"
                :action="ActionButtonStyle.Unlink" 
                text="Disconnect"
                :displayStyle="DisplayStyle.Danger"
                :performAction="disconnectProvider" />

            <MessageSection :displayStyle="DisplayStyle.Danger" :type="MessageSectionType.Row" v-if="0 === (compData.disconnectProviderModal.reason?.length ?? 0)">
                Please enter the reason you are de-activating (above)
            </MessageSection>
        </div>
        
    </ModalDialog>

</template>
